import React, { useMemo } from 'react';
import { loopPoints, scribblePoints, toPathD, underlinePoints } from '../../lib/paper';

interface MarkProps {
  color?: string;
  strokeWidth?: number;
  seed?: number;
  className?: string;
}

/**
 * A loose pen circle, drawn on. Sized by its box (`width`/`height`); position it
 * absolutely over whatever it rings. Ignores clicks.
 */
export const Loop: React.FC<MarkProps & { width: number; height: number }> = ({
  width,
  height,
  color = 'var(--accent)',
  strokeWidth = 2.2,
  seed = 1,
  className = '',
}) => {
  const d = useMemo(() => toPathD(loopPoints(width, height, seed)), [width, height, seed]);
  return (
    <svg aria-hidden width={width} height={height} className={`pointer-events-none ${className}`}>
      <path d={d} pathLength={1} className="pb-draw" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
};

/** A marker swipe that draws itself under a heading. */
export const Underline: React.FC<MarkProps & { width: number; height?: number }> = ({
  width,
  height = 10,
  color = 'var(--accent)',
  strokeWidth = 3,
  seed = 1,
  className = '',
}) => {
  const d = useMemo(() => toPathD(underlinePoints(width, height, seed)), [width, height, seed]);
  return (
    <svg aria-hidden width={width} height={height} className={`pointer-events-none block ${className}`}>
      <path d={d} pathLength={1} className="pb-draw" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
};

/**
 * A pen that keeps writing: a squiggle that redraws with a fresh stroke on a
 * loop. The "reading your handwriting…" state, as on mobile.
 */
export const WritingScribble: React.FC<MarkProps & { width?: number; lines?: number }> = ({
  width = 120,
  lines = 2,
  color = 'var(--accent)',
  strokeWidth = 1.5,
  seed = 1,
  className = '',
}) => {
  const rows = useMemo(
    () =>
      Array.from({ length: lines }, (_, i) =>
        toPathD(scribblePoints(i === 0 ? width : width * 0.62, 9, seed + i)),
      ),
    [width, lines, seed],
  );
  return (
    <svg aria-hidden width={width} height={lines * 16} className={`pointer-events-none block ${className}`}>
      {rows.map((d, i) => (
        <path
          key={i}
          d={d}
          transform={`translate(0 ${i * 16 + 3})`}
          pathLength={1}
          className="pb-write"
          style={{ animationDelay: `${i * 0.5}s` }}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
};
